/**
 * Thiết lập bố cục toàn app. Tách khỏi tokens/ vì đây là lựa chọn bố cục,
 * không phải giá trị thiết kế. Không rải max-width / chiều cao header
 * trong từng màn — sửa ở đây.
 *
 * Đối chiếu defaultSettings.ts của Ant Design Pro: một object phẳng, mọi
 * field có default rõ. Khác pro ở chỗ không có navTheme / colorPrimary —
 * hai thứ đó đã nằm ở useTheme(), không dựng state thứ hai.
 */

export type LayoutMode = 'side' | 'top' | 'mix'
export type ContentWidth = 'Fluid' | 'Fixed'
export type SiderMenuType = 'sub' | 'group'
/** Nền trang: lấy thẳng token `--vbdt-color-bg-<tên>` nên đúng cả sáng lẫn tối. */
export type PageBg = 'page' | 'surface' | 'sunken' | 'custom'
/** Bo góc: vuông (4px) · vừa (8px, mặc định) · tròn (12px) hoặc số pixel tuỳ chọn (0–20px). */
export type RadiusLevel = 'sm' | 'md' | 'lg' | number

export const RADIUS_PX: Record<'sm' | 'md' | 'lg', number> = { sm: 4, md: 8, lg: 12 }

export const getRadiusPx = (level: RadiusLevel): number =>
  typeof level === 'number' ? level : (RADIUS_PX[level] ?? RADIUS_PX.md)

export const MAX_CUSTOM_COLORS = 8

/** Icon của Spin AntD: 'default' là vòng xoay mặc định của AntD, các giá trị còn lại do UiSpin vẽ. */
export const SPIN_ICONS = ['default', 'loading', 'dots', 'orbit', 'dual-ring', 'hourglass'] as const
export type SpinIcon = (typeof SPIN_ICONS)[number]

export const DEFAULT_SPIN_ICON: SpinIcon = 'default'

export const SPIN_ICON_LABEL: Record<SpinIcon, string> = {
  default: 'Mặc định',
  loading: 'Vòng cung',
  dots: 'Ba chấm',
  orbit: 'Quỹ đạo',
  'dual-ring': 'Vòng đôi',
  hourglass: 'Đồng hồ cát',
}

export const isSpinIcon = (value: unknown): value is SpinIcon =>
  SPIN_ICONS.some((icon) => icon === value)

export type LayoutSettings = {
  // Kích thước
  contentMaxWidth: number
  headerHeight: number
  siderWidth: number
  siderCollapsedWidth: number
  // Bố cục
  layout: LayoutMode
  contentWidth: ContentWidth
  siderMenuType: SiderMenuType
  splitMenus: boolean
  fixedHeader: boolean
  fixSiderbar: boolean
  // Vùng hiển thị — đối ứng 'Regional Settings' của antd pro
  showHeader: boolean
  showFooter: boolean
  showMenu: boolean
  showMenuHeader: boolean
  showBreadcrumb: boolean
  // Giao diện
  pageBg: PageBg
  /** Màu nền trang tuỳ chọn (`#RRGGBB`), chỉ dùng khi `pageBg === 'custom'`. */
  customPageBg: string
  /** Màu chủ đạo tuỳ chọn (`#RRGGBB`); rỗng = dùng Theme Color có sẵn. */
  customPrimary: string
  /** Màu chủ đạo người dùng tự thêm (`#RRGGBB`), hiện cạnh preset; tối đa MAX_CUSTOM_COLORS. */
  customColors: string[]
  radiusLevel: RadiusLevel
  compact: boolean
  /** Icon của Spin AntD ở màn công khai. */
  spinIcon: SpinIcon
  // Khác
  colorWeak: boolean
  /**
   * Key của ThemePreset đang active (từ THEME_PRESETS).
   * undefined = không dùng preset — user thiết lập thủ công.
   */
  activePreset?: string
}

/**
 * Mặc định theo theme MBseller (USER chốt 2026-09-29): header 64; kiểu điều hướng đổi sang menu bên trái (USER chốt 2026-10-31).
 */
export const defaultLayoutSettings: LayoutSettings = {
  contentMaxWidth: 1440,
  headerHeight: 64,
  siderWidth: 290,
  siderCollapsedWidth: 52,
  layout: 'side',
  contentWidth: 'Fluid',
  siderMenuType: 'sub',
  splitMenus: false,
  fixedHeader: true,
  fixSiderbar: true,
  showHeader: true,
  showFooter: true,
  showMenu: true,
  showMenuHeader: true,
  showBreadcrumb: true,
  pageBg: 'page',
  customPageBg: '',
  customPrimary: '',
  customColors: [],
  radiusLevel: 'md',
  compact: false,
  colorWeak: false,
  spinIcon: DEFAULT_SPIN_ICON,
}

/** @deprecated Dùng `defaultLayoutSettings`, hoặc `useLayoutSettings()` nếu cần giá trị đang chạy. */
export const layoutSettings = defaultLayoutSettings

export const buildLayoutCssVars = (s: LayoutSettings): Record<string, string> => ({
  '--vbdt-content-max-width': `${s.contentMaxWidth}px`,
  '--vbdt-header-height': `${s.headerHeight}px`,
  '--vbdt-sider-width': `${s.siderWidth}px`,
  '--vbdt-sider-collapsed-width': `${s.siderCollapsedWidth}px`,
  '--vbdt-layout-bg':
    s.pageBg === 'custom' && s.customPageBg
      ? s.customPageBg
      : `var(--vbdt-color-bg-${s.pageBg === 'custom' ? 'page' : s.pageBg})`,
})
